import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import { getToken } from '../lib/api'
import { buildBridgeSkill, passerelleBaseUrl, type SkillPart } from '../lib/bridge-skill'

/**
 * Presents a ready-to-use "skill" (instructions + auth + payload format) the member hands to their
 * OWN AI to update this artefact via the passerelle's /bridge endpoints — without the built-in
 * Claude/Copilot chat. Auto-generated from the artefact's id + the current session token.
 */
export function ExternalAgentModal({
  bid,
  artefactId,
  title,
  parts,
  onClose,
}: {
  bid: string
  artefactId: string
  title: string
  parts: SkillPart[]
  onClose: () => void
}) {
  const { t } = useLang()
  const [copied, setCopied] = useState(false)

  const baseUrl = passerelleBaseUrl()
  const getUrl = `${baseUrl}/bridge/artefact?bid=${encodeURIComponent(bid)}&artefactId=${encodeURIComponent(artefactId)}`
  const skill = useMemo(
    () => buildBridgeSkill({ baseUrl, bid, artefactId, title, token: getToken() ?? '', parts }),
    [baseUrl, bid, artefactId, title, parts],
  )

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(skill)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      /* clipboard unavailable — the textarea below is selectable as a fallback */
    }
  }

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal ext-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('ext_title')}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="modal-head">
          <h2>{t('ext_title')}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={onClose}>✕</button>
        </div>
        <div className="modal-body ext-body">
          <p className="ext-intro">{t('ext_intro')}</p>

          <label className="ext-field">
            <span>{t('ext_link')}</span>
            <input className="ext-link" readOnly value={getUrl} onFocus={(e) => e.currentTarget.select()} />
          </label>

          <div className="ext-skillhead">
            <span>{t('ext_skill')}</span>
            <button className="mini ok" onClick={() => void copy()}>{copied ? t('ext_copied') : t('ext_copy')}</button>
          </div>
          <textarea className="ext-skill" readOnly value={skill} onFocus={(e) => e.currentTarget.select()} />
        </div>
      </div>
    </div>,
    document.body,
  )
}
